/**
 * Emporium Embroidery - canonical pricing model.
 * ---------------------------------------------------------------------------
 * THIS FILE IS THE SINGLE SOURCE OF TRUTH FOR ALL PRICES.
 *
 * Consumed by:
 *   - assets/configurator.config.js   (live quote estimates)
 *   - snippets/ee-pricing-data.liquid (guide page, generated)
 *   - EE-Ops/sync-products.mjs        (Shopify variant prices)
 *   - EE-Ops/sync-metaobjects.mjs     (homepage pricing_row teaser)
 *
 * If a price changes, change it HERE and re-run the sync. Never edit prices in
 * the Shopify admin, the guide page, or the configurator directly.
 *
 * Derivation (Ben's published guide + his WhatsApp rules, 2026-09-02):
 *   - Base ladder: 1.86 at <=25cm2, +0.30 per extra 10cm2 up to 125cm2.
 *     Lands on 4.86 at 115-125cm2 - exactly Ben's published 100-125cm2 price.
 *   - Ben's published 125-150 / 150-175 / 175-200 rows are kept verbatim.
 *   - Colour tier step: +0.30 at <=100cm2, +0.50 above. Reproduces all ten of
 *     Ben's published rows exactly.
 *   - 100% coverage uplift: +0.05 per band to 65cm2 (matching Ben's published
 *     spacing), then +0.20 per band to 2.40 at 125cm2 (his published anchor).
 *
 * All prices are ex VAT, per patch, in GBP.
 */
(function (root) {
  'use strict';

  function r2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

  /* Band comparisons carry a tolerance. A size typed in mm and converted to cm2
     can land a hair over a boundary through floating-point error alone - a
     67.082mm square computes as 45.000000000000006cm2 - which would otherwise
     bump the customer up an entire price band. */
  var EPS = 1e-9;
  function lte(a, b) { return a <= b + EPS; }

  var LADDER_BASE = 1.86;
  var LADDER_STEP = 0.30;

  var SQ = '²';
  var EN = '–';
  function lbl(a, b) { return a + 'cm' + SQ + EN + b + 'cm' + SQ; }

  /* Bands 0-10 are the 0.30 ladder. Bands 11-13 are Ben's published rows,
     kept verbatim and flagged so the guide can annotate them. */
  var BANDS = [
    { min: 0,   max: 25,  label: 'Up to 25cm' + SQ },
    { min: 25,  max: 35,  label: lbl(25, 35) },
    { min: 35,  max: 45,  label: lbl(35, 45) },
    { min: 45,  max: 55,  label: lbl(45, 55) },
    { min: 55,  max: 65,  label: lbl(55, 65) },
    { min: 65,  max: 75,  label: lbl(65, 75) },
    { min: 75,  max: 85,  label: lbl(75, 85) },
    { min: 85,  max: 95,  label: lbl(85, 95) },
    { min: 95,  max: 105, label: lbl(95, 105) },
    { min: 105, max: 115, label: lbl(105, 115) },
    { min: 115, max: 125, label: lbl(115, 125) },
    { min: 125, max: 150, label: lbl(125, 150), p50: 7.56,  uplift: 2.40, published: true },
    { min: 150, max: 175, label: lbl(150, 175), p50: 10.26, uplift: 4.10, published: true },
    { min: 175, max: 200, label: lbl(175, 200), p50: 14.26, uplift: 4.60, published: true }
  ];

  function bandBase(i) {
    var b = BANDS[i];
    if (b.p50 != null) return b.p50;
    return r2(LADDER_BASE + LADDER_STEP * i);
  }

  /* 100% coverage uplift.
     i 0-4  : 1.00 +0.05/band -> 1.00 1.05 1.10 1.15 1.20      (matches Ben)
     i 5-10 : 1.20 +0.20/band -> 1.40 1.60 1.80 2.00 2.20 2.40 (lands on anchor)
     i 11+  : Ben's published uplift, verbatim */
  function bandUplift(i) {
    var b = BANDS[i];
    if (b.uplift != null) return b.uplift;
    if (i <= 4) return r2(1.00 + 0.05 * i);
    return r2(1.20 + 0.20 * (i - 4));
  }

  function tierStep(area) { return area > 100 ? 0.50 : 0.30; }

  /* Printed patches (Ben, 2026-09-03): "50p cheaper and the rest follow the same
     pricing structure". Same size bands, same backing add-ons, same set-up fees
     and same batch discounts as embroidery - just 50p off the per-patch price.
     There is no colour tier or coverage choice: DTF printing is full colour by
     nature, so the baseline is the 50% / 1-3 colour column. */
  var PRINTED_DISCOUNT = 0.50;
  function printedBandPrice(i) { return r2(bandBase(i) - PRINTED_DISCOUNT); }

  var EXTRA_COLOUR = 0.10;
  var QUOTE_ABOVE_CM2 = 200;
  var BUYABLE_MAX_CM2 = 125;
  var MIN_BUYABLE_QTY = 25;

  /* Add-ons: [value at 25cm2, value at 200cm2], linear in between. */
  var BACKING_ADDONS = {
    'Sew-on':               [0, 0],
    'Iron-on':              [0.10, 0.50],
    'Adhesive':             [0.15, 0.55],
    'Velcro hook only':     [0.60, 6.00],
    'Velcro hook and loop': [0.90, 9.00],
    'Premium felt sew-on':  null
  };
  var SPECIAL_ADDON = [0.30, 2.50];
  var SETUP_FEES = [[100, 7.50], [150, 9], [175, 12], [200, 14], [225, 18], [250, 20]];

  /* Batch discounts (Ben, 2026-09-02). Highest qualifying tier wins. */
  var BATCH_TIERS = [
    { qty: 1000, pct: 0.33, label: '33% off' },
    { qty: 500,  pct: 0.20, label: '20% off' },
    { qty: 200,  pct: 0.15, label: '15% off' },
    { qty: 100,  pct: 0.10, label: '10% off' },
    { qty: 50,   pct: 0.05, label: '5% off' },
    { qty: 25,   pct: 0,    label: 'Free set-up and digitising' }
  ];

  var COLOUR_TIERS = [
    { idx: 0, code: 'C13', label: '1' + EN + '3 colours' },
    { idx: 1, code: 'C46', label: '4' + EN + '6 colours' },
    { idx: 2, code: 'C79', label: '7' + EN + '9 colours' }
  ];
  var BACKING_CODES = {
    'Sew-on': 'SEW', 'Iron-on': 'IRN', 'Adhesive': 'ADH',
    'Velcro hook only': 'VHO', 'Velcro hook and loop': 'VHL'
  };

  function scaleByArea(range, area) {
    var t = Math.max(0, Math.min(1, (area - 25) / (200 - 25)));
    return range[0] + (range[1] - range[0]) * t;
  }

  /** True when a patch is too big for any published price. */
  function isQuoteOnly(area) { return !area || !lte(area, QUOTE_ABOVE_CM2); }

  function bandIndex(area) {
    for (var i = 0; i < BANDS.length; i++) if (lte(area, BANDS[i].max)) return i;
    return -1;
  }

  /* Price for a band at a coverage + colour tier, evaluated at the band's TOP
     area so it always matches the guide row and never underprices a variant. */
  function bandPrice(i, coverage, tierIdx) {
    var area = BANDS[i].max;
    var p = bandBase(i);
    if (coverage === '100') p += bandUplift(i);
    p += tierStep(area) * (tierIdx || 0);
    return r2(p);
  }

  /* Shopify variant price: band price + backing add-on, both at band top. */
  function variantPrice(i, coverage, tierIdx, backing) {
    var b = BACKING_ADDONS[backing];
    if (b === null || b === undefined) return null;
    return r2(bandPrice(i, coverage, tierIdx) + scaleByArea(b, BANDS[i].max));
  }

  /* Full per-patch unit price for an exact spec. */
  function unitPrice(o) {
    var area = o.area;
    if (isQuoteOnly(area)) return null;
    var i = bandIndex(area);
    if (i < 0) return null;

    var p = bandPrice(i, o.coverage, o.tierIdx || 0);
    if (o.extraColours) p += EXTRA_COLOUR * o.extraColours;

    var backing = BACKING_ADDONS[o.backing || 'Sew-on'];
    if (backing === null) return null;
    if (backing) p += scaleByArea(backing, area);

    (o.specials || []).forEach(function () { p += scaleByArea(SPECIAL_ADDON, area); });
    return r2(p);
  }

  function setupFee(area) {
    for (var i = 0; i < SETUP_FEES.length; i++) if (lte(area, SETUP_FEES[i][0])) return SETUP_FEES[i][1];
    return null;
  }

  function batchTier(qty) {
    for (var i = 0; i < BATCH_TIERS.length; i++) if (qty >= BATCH_TIERS[i].qty) return BATCH_TIERS[i];
    return null;
  }
  function batchDiscount(qty) { var t = batchTier(qty); return t ? t.pct : 0; }
  function nextTier(qty) {
    var next = null;
    BATCH_TIERS.forEach(function (t) { if (t.qty > qty && (!next || t.qty < next.qty)) next = t; });
    return next;
  }

  /* Buyable buckets ARE the ladder bands up to 125cm2, so a variant price can
     never disagree with the guide. Split into two Shopify products per
     coverage because 11 x 3 x 5 = 165 exceeds the 100-variant cap. */
  var SIZE_BUCKETS = [];
  for (var bi = 0; bi < 11; bi++) {
    SIZE_BUCKETS.push({
      index: bi,
      max: BANDS[bi].max,
      label: BANDS[bi].label,
      group: BANDS[bi].max <= 65 ? 'standard' : 'large',
      code: 'B' + bi
    });
  }

  function bucketFor(area) {
    if (!area || !lte(area, BUYABLE_MAX_CM2)) return null;
    var i = bandIndex(area);
    return i >= 0 && i < SIZE_BUCKETS.length ? SIZE_BUCKETS[i] : null;
  }

  function sku(coverage, bucketCode, tierCode, backingName) {
    return ['EMB' + coverage, bucketCode, tierCode, BACKING_CODES[backingName]].join('-');
  }

  /* ── Printed ──────────────────────────────────────────────────────────── */

  function printedVariantPrice(i, backing) {
    var b = BACKING_ADDONS[backing];
    if (b === null || b === undefined) return null;
    return r2(printedBandPrice(i) + scaleByArea(b, BANDS[i].max));
  }

  function printedUnitPrice(o) {
    var area = o.area;
    if (isQuoteOnly(area)) return null;
    var i = bandIndex(area);
    if (i < 0) return null;
    var p = printedBandPrice(i);
    var backing = BACKING_ADDONS[o.backing || 'Sew-on'];
    if (backing === null) return null;
    if (backing) p += scaleByArea(backing, area);
    return r2(p);
  }

  function printedSku(bucketCode, backingName) {
    return ['PRT', bucketCode, BACKING_CODES[backingName]].join('-');
  }

  /* Preset areas for the set-size printed product, as bounding boxes in cm2. */
  var PRINTED_PRESET_AREAS = {
    'XS (25 × 25mm)': 6.25, 'S (50 × 50mm)': 25, 'M (75 × 75mm)': 56.25,
    'L (100 × 100mm)': 100, 'XL (125 × 125mm)': 156.25,
    'XS (25mm)': 6.25, 'S (50mm)': 25, 'M (75mm)': 56.25,
    'L (100mm)': 100, 'XL (125mm)': 156.25,
    'S (25 × 80mm)': 20, 'M (40 × 120mm)': 48,
    'L (60 × 145mm)': 87, 'XL (80 × 160mm)': 128
  };

  function matrix() {
    return BANDS.map(function (b, i) {
      return {
        label: b.label, min: b.min, max: b.max, published: !!b.published,
        cells: [
          bandPrice(i, '50', 0), bandPrice(i, '100', 0),
          bandPrice(i, '50', 1), bandPrice(i, '100', 1),
          bandPrice(i, '50', 2), bandPrice(i, '100', 2)
        ]
      };
    });
  }

  var api = {
    BANDS: BANDS, SIZE_BUCKETS: SIZE_BUCKETS, COLOUR_TIERS: COLOUR_TIERS,
    BACKING_ADDONS: BACKING_ADDONS, BACKING_CODES: BACKING_CODES,
    SPECIAL_ADDON: SPECIAL_ADDON, SETUP_FEES: SETUP_FEES, BATCH_TIERS: BATCH_TIERS,
    EXTRA_COLOUR: EXTRA_COLOUR, QUOTE_ABOVE_CM2: QUOTE_ABOVE_CM2,
    BUYABLE_MAX_CM2: BUYABLE_MAX_CM2, MIN_BUYABLE_QTY: MIN_BUYABLE_QTY,
    r2: r2, lte: lte, isQuoteOnly: isQuoteOnly,
    scaleByArea: scaleByArea, bandIndex: bandIndex, bandBase: bandBase,
    bandUplift: bandUplift, tierStep: tierStep, bandPrice: bandPrice,
    variantPrice: variantPrice, unitPrice: unitPrice, setupFee: setupFee,
    batchTier: batchTier, batchDiscount: batchDiscount, nextTier: nextTier,
    bucketFor: bucketFor, sku: sku, matrix: matrix,
    PRINTED_DISCOUNT: PRINTED_DISCOUNT, PRINTED_PRESET_AREAS: PRINTED_PRESET_AREAS,
    printedBandPrice: printedBandPrice, printedVariantPrice: printedVariantPrice,
    printedUnitPrice: printedUnitPrice, printedSku: printedSku
  };

  root.EEPricing = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
